import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api/client';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useAuth, type Session } from '../../store/auth';
import { ErrorNotice } from '../../components/ui';

const SHOP_CODE_KEY = 'aurum.shop';

/**
 * The way into the shop.
 *
 * The shop code is remembered between visits — it is the same every morning for
 * everybody who works there, and it is not a secret; the password never is,
 * which is why the token lives in memory and nothing about it is written down.
 *
 * Caps lock gets its own warning because a jeweller's password is typed on a
 * counter keyboard in a hurry, and "that username or password is not correct"
 * three times running, when the only problem is a stuck key, is how somebody
 * locks themselves out.
 */
export function LoginPage() {
  const signIn = useAuth((s) => s.signIn);
  const onEnter = useKeyboardForm();
  const [tenantCode, setTenantCode] = useState(() => localStorage.getItem(SHOP_CODE_KEY) ?? '');
  // The shop code is asked for only when the username alone cannot say which
  // shop is meant — the same name in use at two shops on one installation.
  const [needShop, setNeedShop] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [capsLock, setCapsLock] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  // Sign-in starts where the typing does.
  useEffect(() => {
    document.getElementById(needShop ? 'tenant' : 'username')?.focus();
  }, [needShop]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await api.post<Session>('/auth/login', {
        username,
        password,
        ...(needShop && tenantCode ? { tenantCode } : {}),
      });
      if (tenantCode) localStorage.setItem(SHOP_CODE_KEY, tenantCode);
      signIn(session);
    } catch (err) {
      // One username, two shops: ask for the code rather than guessing.
      if (err instanceof ApiError && err.problem.code === 'AUTH_SHOP_NEEDED') {
        setNeedShop(true);
        setError(err);
        setBusy(false);
        return;
      }
      setError(err instanceof ApiError ? err : new Error('Could not reach the server. Is it running?'));
      setPassword('');
      document.getElementById('password')?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <div className="mb-5 text-center">
          {/* A hallmark punch: the mark a piece carries to say what it is. */}
          <svg viewBox="0 0 40 40" className="mx-auto h-9 w-9 text-gold-600" aria-hidden="true">
            <path
              d="M20 3.5 33 11v18L20 36.5 7 29V11z"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <path d="M15 25V15l5 6 5-6v10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <h1 className="mt-2 text-xl font-semibold tracking-tight">Aurum</h1>
          <p className="mt-0.5 text-sm text-ink-soft">Sign in to your shop</p>
        </div>

        <form autoComplete="off" onKeyDown={onEnter} onSubmit={(e) => void submit(e)} className="card">
          <ErrorNotice error={error} />

          {needShop ? (
            <>
              <label className="label" htmlFor="tenant">
                Shop code
              </label>
              <input
                id="tenant"
                className="field num mb-3"
                value={tenantCode}
                autoComplete="organization"
                maxLength={32}
                onChange={(e) => setTenantCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              />
            </>
          ) : null}

          <label className="label" htmlFor="username">
            Username
          </label>
          <input
            id="username"
            className="field num mb-3"
            value={username}
            autoComplete="username"
            maxLength={64}
            onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
          />

          <label className="label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            type="password"
            className="field"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            onKeyUp={(e) => setCapsLock(e.getModifierState('CapsLock'))}
          />
          {capsLock ? (
            <p className="mt-1 text-2xs text-warn">Caps lock is on.</p>
          ) : null}

          {/* The shop code is only asked for when a username is used at two
              shops, so it must not be part of what enables the button — that
              left Sign in dead on a fresh machine even with both fields typed. */}
          <button className="btn-primary mt-5 w-full" disabled={busy || !username || !password || (needShop && !tenantCode)}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-4 text-center text-2xs text-ink-faint">
          Forgotten your password? Ask the shop owner to reset it — nobody else can.
        </p>
      </div>
    </div>
  );
}
